import { t } from '@lingui/core/macro'
import _ from 'lodash'
import { LinkValidationPoller } from 'handsontable/utils/linkValidationPoller'
import { UrlAuditRoutes } from 'routes'
import { linkQueue } from './link.queue'
import { TypeUrlData } from './urlData.interface'
import { api } from 'api'

interface TypeUrlError {
  completed_at: string
  json_body: {
    error: boolean
    errorMessage: string
  }
  error: boolean
  status: number
  url: string
}
interface TypeValidationResults {
  dataset_rows: any[]
  linkFieldId: number
}
interface TypeValidationLinks {
  urls: TypeUrlData[]
}
interface TypeHandleValidationError {
  urls: any[]
}
export class LinkManager {
  private static instance: LinkManager

  private dataContainer: any

  private UrlAudits: any

  private templateId: number

  private constructor(dataContainer: any, UrlAudits: any, templateId: number) {
    this.dataContainer = dataContainer
    this.UrlAudits = UrlAudits
    this.templateId = templateId
  }

  public static getInstance(dataContainer: any, UrlAudits: any, templateId: number): LinkManager {
    if (!LinkManager.instance) {
      LinkManager.instance = new LinkManager(dataContainer, UrlAudits, templateId)
    } else {
      const linkManager = LinkManager.instance
      linkManager.dataContainer = dataContainer
      linkManager.UrlAudits = UrlAudits
      linkManager.templateId = templateId
    }

    return LinkManager.instance
  }

  // statically collect all validation calls in a debounce to group them together and only hit the api once
  private static collectValidationCalls = _.debounce(() => {
    LinkManager.instance.executeValidationQueue()
  }, 1000)

  private createUrlBatch(dataset_rows: any[], linkFieldId: number): TypeUrlData[] {
    // return a list of the link fields from the dataset
    return dataset_rows.reduce((memo, item) => {
      const url = item.dataset_values?.find(dv => dv.field_id == linkFieldId)
      if (url?.cell_value) {
        const exists = this.UrlAudits.findIndex(url.cell_value)
        if (exists === -1) {
          memo.push(this.getUrlData(url.cell_value))
        }
      }
      return memo
    }, [])
  }

  private getUrlData(url: string): TypeUrlData {
    return {
      url,
      created: false,
      cvid: null,
    }
  }

  private createErrorForUrl(url: string): TypeUrlError {
    return {
      completed_at: Date.toLocaleString(),
      json_body: {
        error: true,
        errorMessage: t`limit exceeded`,
      },
      status: 1,
      error: true,
      url,
    }
  }

  // called when a dataset is loaded. First checks for previously validated links with the dataset_uuid
  // then validates any links without results
  getValidationResultsOnLoad({ dataset_rows, linkFieldId }: TypeValidationResults): void {
    api
      .get(UrlAuditRoutes.results(this.dataContainer.datasetUuid))
      .then(({ data }) => {
        this.UrlAudits.add(data)
        this.dataContainer.forceRender()
        const links = this.createUrlBatch(dataset_rows, linkFieldId)

        if (links.length) {
          this.validateLinks({
            urls: links,
          })
        }
      })
      .catch(() => {
        this.handleValidationError({
          urls: this.createUrlBatch(dataset_rows, linkFieldId),
        })
      })
  }

  // add links to the queue any time a change is made to a link field (on enter, paste, ect.)
  // starts the debounce timer for collecting validation calls
  queueValidationResultsOnChange(link: TypeUrlData): void {
    linkQueue.addLinkToQueue(this.dataContainer.datasetUuid, link)
    LinkManager.collectValidationCalls()
  }

  // renders an error message on rows that are unsuccessful or when an api call generally fails
  private handleValidationError({ urls }: TypeHandleValidationError): void {
    const results = urls.map(url => this.createErrorForUrl(url.url))
    this.UrlAudits.add(results)
    this.dataContainer.forceRender()
  }

  // sends a post request with an array of urls for flipper to validate
  // on success sets up polling for these urls
  private validateLinks({ urls }: TypeValidationLinks): void {
    const urlsNotValidated = urls.filter(({ created }) => !created)
    if (urlsNotValidated.length) {
      api
        .post(UrlAuditRoutes.validateUrls, {
          dataset_uuid: this.dataContainer.datasetUuid,
          template_id: this.templateId,
          urls: urlsNotValidated,
        })
        .then(() => {
          this.setupPolling()
        })
        .catch(() => {
          this.handleValidationError({ urls })
        })
    } else {
      this.setupPolling()
    }
  }

  private setupPolling(): void {
    const poller = LinkValidationPoller.getPoller(this.dataContainer.datasetUuid)
    poller.onResults(results => {
      this.UrlAudits.add(results)
      this.dataContainer.forceRender()
    })
    poller.start()
  }

  // validates all urls currently in the queue
  private executeValidationQueue(): void {
    const linksInQueue = linkQueue.getQueue(this.dataContainer.datasetUuid)
    // empty queue after grabbing urls to prevent links being sent to flipper multiple times
    linkQueue.emptyQueue(this.dataContainer.datasetUuid)

    api
      .get(UrlAuditRoutes.results(this.dataContainer.datasetUuid))
      .then(({ data: urlValidationResults }) => {
        const unvalidatedLinks = this.getUnvalidatedLinks(linksInQueue, urlValidationResults)

        if (unvalidatedLinks.length) {
          this.validateLinks({ urls: unvalidatedLinks })
        }
      })
      .catch(() => {
        this.handleValidationError({ urls: linksInQueue })
      })
  }

  private getUnvalidatedLinks(links: TypeUrlData[], validationResults: any[]): TypeUrlData[] {
    // const cleanUrl = str => str.split('?')[0]
    // commenting this out for now, report suite ids and other checks are failing when the query params are removed.
    // eventually want to incorportate this as a config step on link validations

    if (validationResults.length == 0) return links

    return links.map(link => {
      const validatedUrl = validationResults.find(result => result.url == link.url)

      if (validatedUrl?.created_at) {
        link.created = true
      }
      return link
    })
  }
}
